import TicketTemplate, {
  type OnCreateTicketArgs,
} from "../TicketTemplate/TicketTemplate.js";
import React from "react";
import { useMutation } from "@apollo/client";
import { MANUAL_TICKET } from "../TicketingAPI.js";
import { isUndefinedOrNull } from "@SeemplicityDev/shared";
import useInternalManualTicketForm from "./useInternalManualTicketForm.js";

interface OpenedTicket {
  ticket_due_date_remaining_time?: string;
  ticket_due_date_status?: string;
  due_date?: string;
  id?: string;
  status?: string;
  ticket_provider?: {
    known_identifier?: string;
    type?: string;
  };
  link?: string;
  external_id?: string;
  external_status?: string;
}

export interface InternalManualTicketFormProps {
  // TODO: filters_config flows through to a GraphQL FiltersConfigInput; typed as
  // unknown to match the package convention until that input shape is modeled.
  filters_config?: unknown;
  closeModal: () => void;
  onError?: (error: { message: string }) => void;
  onSuccess?: (
    ticket: OpenedTicket & { ticketManagerFriendlyName?: string }
  ) => void;
  fieldMappingId?: string;
  ticketManagerFriendlyName?: string;
  ticketManagerId?: string;
  ticketManagerMustHaveMapping?: boolean;
  ticketManagerMustHaveColumnsMapping?: boolean;
  // TODO: ticketManagerProviders / keys originate from untyped .js callers; typed
  // as unknown until the upstream provider/keys shapes are modeled.
  ticketManagerProviders?: unknown;
  ticketProviderId?: string;
  keys?: unknown;
  wrapperClassName?: string;
  onCreateMapping?: (...args: unknown[]) => void;
  onCreateMappingError?: (...args: unknown[]) => void;
  onEventTriggered?: (...args: unknown[]) => void;
  rmqId?: string;
}

const InternalManualTicketForm: React.FC<InternalManualTicketFormProps> = ({
  filters_config,
  closeModal,
  onError = () => {},
  onSuccess = () => {},
  fieldMappingId,
  ticketManagerFriendlyName,
  ticketManagerId,
  ticketManagerMustHaveMapping,
  ticketManagerMustHaveColumnsMapping,
  ticketManagerProviders,
  ticketProviderId,
  keys,
  wrapperClassName = "",
  onCreateMapping = () => {},
  onCreateMappingError = () => {},
  onEventTriggered = () => {},
  rmqId,
}) => {
  const { setIsSubmitting, childRef, modalButtons, setTicketTemplateValid } =
    useInternalManualTicketForm({
      closeModal,
    });
  const [createTicket] = useMutation(MANUAL_TICKET);

  const onSubmit = ({ formData, mutationData }: OnCreateTicketArgs) => {
    const duedate = formData?.fields?.duedate;

    let variables: Record<string, unknown> = {
      filters_config,
      duedate,
      value_mapping: mutationData,
      ticket_provider_id: formData?.ticketProvider,
    };

    if (!isUndefinedOrNull(rmqId)) {
      variables.remediation_queue_id = rmqId;
    }

    createTicket({
      variables,
    })
      .then((res) => {
        if (!!res?.data?.open_ticket?.ticket) {
          onSuccess({
            ...res?.data?.open_ticket?.ticket,
            ticketManagerFriendlyName,
          });
          closeModal();
        }
      })
      .catch((err) => {
        onError({ message: err.message });
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <TicketTemplate
      ref={childRef}
      modalButtonsContainer={modalButtons}
      onCreateTicket={onSubmit}
      ticketManagerId={ticketManagerId}
      ticketManagerFriendlyName={ticketManagerFriendlyName}
      ticketManagerMustHaveMapping={ticketManagerMustHaveMapping}
      ticketManagerMustHaveColumnsMapping={ticketManagerMustHaveColumnsMapping}
      filters_config={filters_config}
      ticketManagerProviders={
        // ticketManagerProviders is `unknown` (untyped .js caller); the runtime
        // value is the provider list TicketTemplate expects. Type-only cast.
        ticketManagerProviders as Array<{ id?: string }> | undefined
      }
      ticketProviderId={ticketProviderId}
      keys={keys}
      fieldMappingId={fieldMappingId}
      onEventTriggered={onEventTriggered}
      onCreateMapping={onCreateMapping}
      onCreateMappingError={onCreateMappingError}
      wrapperClassName={wrapperClassName}
      onValidationChange={(isValid: boolean) => {
        setTicketTemplateValid(isValid);
      }}
    />
  );
};

export default InternalManualTicketForm;
